import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IncomeDisclosureService } from "JS/Services/IncomeDisclosure";
import { logoutUser } from "JS/Services/BaseService";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { IncomeDisclosure } from "JS/Models/IncomeDisclosure";
import {
    getAcceptedIncomeDisclosureVersion,
    isIncomeDisclosureAccepted,
    setAcceptedIncomeDisclosureVersion,
} from "JS/Helpers/IncomeDisclosureStorage";

export type IncomeDisclosureGateStatus =
    | "hidden"
    | "checking"
    | "show"
    | "loadError";

export const useIncomeDisclosureGate = (memberId: string) => {
    const service = useMemo(() => new IncomeDisclosureService(), []);
    const [status, setStatus] = useState<IncomeDisclosureGateStatus>(() =>
        getAcceptedIncomeDisclosureVersion(memberId) ? "hidden" : "checking",
    );
    const [disclosure, setDisclosure] = useState<IncomeDisclosure | null>(
        null,
    );
    const [accepting, setAccepting] = useState(false);
    const [acceptError, setAcceptError] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [offlineNotice, setOfflineNotice] = useState(false);
    const fetchingRef = useRef(false);
    const acceptingRef = useRef(false);
    const acceptedAtRef = useRef<number | null>(null);
    const shownVersionRef = useRef<string | null>(null);

    const check = useCallback(async () => {
        if (fetchingRef.current || acceptingRef.current) return;
        fetchingRef.current = true;
        try {
            const file = await service.getIncomeDisclosure();
            if (isIncomeDisclosureAccepted(memberId, file.version)) {
                shownVersionRef.current = null;
                setDisclosure(null);
                setStatus("hidden");
                return;
            }
            if (shownVersionRef.current !== String(file.version)) {
                shownVersionRef.current = String(file.version);
                acceptedAtRef.current = null;
                setAcceptError(false);
            }
            setDisclosure(file);
            setStatus("show");
        } catch (error) {
            setStatus("loadError");
            captureSentryError(error, { location: "IncomeDisclosureFetch" });
        } finally {
            fetchingRef.current = false;
        }
    }, [memberId, service]);

    const retry = useCallback(() => {
        setStatus("checking");
        void check();
    }, [check]);

    const accept = useCallback(async () => {
        if (!disclosure || acceptingRef.current) return;
        if (!globalThis.navigator.onLine) {
            setOfflineNotice(true);
            return;
        }
        if (acceptedAtRef.current === null) acceptedAtRef.current = Date.now();
        acceptingRef.current = true;
        setAccepting(true);
        setAcceptError(false);
        try {
            await service.acceptIncomeDisclosure(
                disclosure.version,
                acceptedAtRef.current,
            );
            setAcceptedIncomeDisclosureVersion(memberId, disclosure.version);
            acceptedAtRef.current = null;
            shownVersionRef.current = null;
            setStatus("hidden");
        } catch (error) {
            setAcceptError(true);
            captureSentryError(error, { location: "IncomeDisclosureAccept" });
        } finally {
            acceptingRef.current = false;
            setAccepting(false);
        }
    }, [disclosure, memberId, service]);

    const decline = useCallback(() => {
        if (!globalThis.navigator.onLine) {
            setOfflineNotice(true);
            return;
        }
        setDeclining(true);
        void logoutUser();
    }, []);

    const dismissOfflineNotice = useCallback(() => setOfflineNotice(false), []);

    useEffect(() => {
        void check();
    }, [check]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") void check();
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, [check]);

    return {
        status,
        disclosure,
        accepting,
        acceptError,
        declining,
        offlineNotice,
        accept,
        decline,
        retry,
        dismissOfflineNotice,
    };
};
